import { z } from "zod";

// Job search filter schema
export const jobSearchFiltersSchema = z.object({
  query: z.string().optional(),
  location: z.string().optional(),
  salaryMin: z.string().optional(),
  salaryMax: z.string().optional(),
  seniority: z.string().optional(),
  category: z.string().optional(),
  employmentType: z.string().optional(),
  workType: z.string().optional(),
  radius: z.number().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  // Enhanced location fields
  locationType: z.enum(['country', 'province', 'city', 'other']).optional(),
  country_code: z.string().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
  // Viewport bounds for geographic searches
  viewport: z.object({
    northeast: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
    southwest: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }).optional(),
});

// Pagination parameters schema
export const paginationSchema = z.object({
  page: z.number().min(1).default(1),
  limit: z.number().min(1).max(100).default(20),
  cursor: z.string().optional(),
});

// Job search response schema
export const jobSearchResponseSchema = z.object({
  jobs: z.array(z.any()), // Will be typed with Job type
  totalCount: z.number(),
  totalPages: z.number(),
  currentPage: z.number(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
  nextCursor: z.string().optional(),
});

// Cursor-based pagination response schema
export const cursorJobResponseSchema = z.object({
  jobs: z.array(z.any()),
  nextCursor: z.string().optional(),
  hasMore: z.boolean(),
  totalCount: z.number().optional(),
});

// Search state schema for frontend
export const searchStateSchema = z.object({
  hasSearched: z.boolean().default(false),
  initialLoad: z.boolean().default(true),
  geolocationStatus: z.enum(['pending', 'granted', 'denied', 'unavailable']).default('pending'),
});

// Geolocation request schema
export const geolocationRequestSchema = z.object({
  requestLocation: z.boolean().default(false),
  useHighAccuracy: z.boolean().default(false),
  timeout: z.number().default(10000),
  maximumAge: z.number().default(300000), // 5 minutes
});

// Export types
export type JobSearchFilters = z.infer<typeof jobSearchFiltersSchema>;
export type PaginationParams = z.infer<typeof paginationSchema>;
export type JobSearchResponse = z.infer<typeof jobSearchResponseSchema>;
export type CursorJobResponse = z.infer<typeof cursorJobResponseSchema>;
export type SearchState = z.infer<typeof searchStateSchema>;
export type GeolocationRequest = z.infer<typeof geolocationRequestSchema>;

const PUBLIC_JOB_FILTER_KEYS = [
  'location',
  'salaryMin',
  'salaryMax',
  'seniority',
  'category',
  'employmentType',
  'workType',
  'country_code',
  'state',
  'city',
] as const;

export function parsePublicJobSearchFilters(search: string | URLSearchParams): JobSearchFilters {
  const params = typeof search === 'string'
    ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    : search;
  const filters: JobSearchFilters = {};
  const query = params.get('q') || params.get('query');

  if (query?.trim()) filters.query = query.trim();
  for (const key of PUBLIC_JOB_FILTER_KEYS) {
    const value = params.get(key);
    if (value?.trim() && value !== 'all') filters[key] = value.trim();
  }

  for (const key of ['radius', 'latitude', 'longitude'] as const) {
    const value = Number(params.get(key));
    if (Number.isFinite(value) && params.has(key)) filters[key] = value;
  }

  const locationType = params.get('locationType');
  if (locationType === 'country' || locationType === 'province' || locationType === 'city' || locationType === 'other') {
    filters.locationType = locationType;
  }

  const viewportValues = {
    northeast: {
      lat: Number(params.get('viewport_ne_lat')),
      lng: Number(params.get('viewport_ne_lng')),
    },
    southwest: {
      lat: Number(params.get('viewport_sw_lat')),
      lng: Number(params.get('viewport_sw_lng')),
    },
  };
  if (
    params.has('viewport_ne_lat')
    && params.has('viewport_ne_lng')
    && params.has('viewport_sw_lat')
    && params.has('viewport_sw_lng')
    && Object.values(viewportValues.northeast).every(Number.isFinite)
    && Object.values(viewportValues.southwest).every(Number.isFinite)
  ) {
    filters.viewport = viewportValues;
  }

  return filters;
}

export function buildPublicJobSearchQuery(filters: JobSearchFilters): string {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === '' || value === 'all') return;
    if (key === 'viewport' && typeof value === 'object') {
      params.set('viewport_ne_lat', value.northeast.lat.toString());
      params.set('viewport_ne_lng', value.northeast.lng.toString());
      params.set('viewport_sw_lat', value.southwest.lat.toString());
      params.set('viewport_sw_lng', value.southwest.lng.toString());
      return;
    }
    params.set(key === 'query' ? 'q' : key, value.toString());
  });

  return params.toString();
}

export function buildCursorJobSearchQuery(
  filters: JobSearchFilters,
  cursor?: string,
  limit = 20,
): string {
  const params = new URLSearchParams({
    type: 'cursor',
    limit: limit.toString(),
  });
  if (cursor) params.set('cursor', cursor);

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === '' || value === 'all') return;
    if (key === 'viewport' && typeof value === 'object') {
      params.set('viewport_ne_lat', value.northeast.lat.toString());
      params.set('viewport_ne_lng', value.northeast.lng.toString());
      params.set('viewport_sw_lat', value.southwest.lat.toString());
      params.set('viewport_sw_lng', value.southwest.lng.toString());
      return;
    }
    params.set(key, value.toString());
  });

  return params.toString();
}